import env from '@env';

import { DBSchema, deleteDB, IDBPDatabase, openDB, StoreNames, StoreKey, StoreValue } from 'idb';

import { NetPatch, PatchId, PatchNature } from '@archipad-js/core/patch';
import { TempPatch, CURRENT_PATCH } from '@archipad/backend/archiweb/synchroDriver';
import { UpgradeBlockedIndexedDBError, BlockingUpgradeIndexedDBError } from '@archipad/errors/errors-archipad';
import { ErrorHandlerService } from '@core/services/errorHandler';

import * as Navigation from "@ui-archipad/drivers/navigation";
import { Participant } from '@archipad/backend/archiweb/sharedProjects';
import { UserCache } from '@archipad/services/account.service';
import { DependencyBundle } from "@archipad/backend/project/dependenciesManager";
import { ProjectDependenciesData } from '@archipad/services/dependenciesStore.service';
import { AttachmentCache } from '@archipad/services/async-upload-attachment.service';
import { LocalProjectInfo } from '@archipad/backend/project/projectInfo';

const config: { dbName: string } = { ...env["@archipad/backend/indexedDB"], dbName: 'app.db' };
export const DB_NAME: string = config.dbName;
const DB_VERSION: number = 5;


export interface ProjectParticipantsCache {
    projectId: string;
    participants: Participant[];
    cacheTimestamp: number;
}
export interface ProjectDependenciesCache {
    /**
     * The key of the cache, which is the projectId
     * @example 'projectId-dependencies_A-dependencies_B-...'
     */
    key: string;
    projectId: string;
    dependencies: string[];
    cacheTimestamp: number;
    data: ProjectDependenciesData;
}
export interface DependencyCache {
    key: string;
    bundles: DependencyBundle[];
    cacheTimestamp: number;
}

export type LocalPatchData<T extends PatchId = PatchId> = T extends typeof CURRENT_PATCH ? TempPatch : NetPatch;

interface AppDBSchema_V1 extends DBSchema {
    'Projects': {
        key: string;
        value: {
            projectId: string;
            info: LocalProjectInfo;
        };
    };

    'ProjectPatches': {
        key: string;
        value: {
            patchId: string;
            data: TempPatch | NetPatch;
        };
    };
}

interface AppDBSchema_V2 extends AppDBSchema_V1 {
    'Interactions': {
        key: string;
        value: {
            interactionId: string;
            data: {
                id: string;
                interactions: unknown;
            }
        };
    };
}

interface AppDBSchema_V3 extends AppDBSchema_V2 {
    'UserReportTemplateConfigurations': {
        key: string;
        value: {
            reportTemplateId: string;
            data: /* SerializableTempReportTemplateConfiguration */ unknown;
        };
    }
}



interface AppDBSchema_V4 extends AppDBSchema_V3 {
    'ProjectPatches': {
        key: string;
        value: {
            patchId: string;
            data: TempPatch | NetPatch;
            nature: PatchNature;
        };
        indexes: {
            key: string;
        }
    };

}

interface AppDBSchema_V5 extends AppDBSchema_V4 {
    'ProjectParticipants': {
        key: string;
        value: ProjectParticipantsCache;
    };
    'Dependencies': {
        key: string;
        value: DependencyCache;
    };
    'UserCache': {
        key: string;
        value: {
            key: typeof USER_CACHE_KEY;
            data: UserCache;
        };
    },
    'ProjectDependencies': {
        key: string;
        value: ProjectDependenciesCache;
    };
    'AttachmentCache': {
        key: string;
        value: {
            key: string;
            data: AttachmentCache;
        }
    };
}
export const USER_CACHE_KEY = 'latest';

// the current db schema version
type AppDBSchema = AppDBSchema_V5;

export type AppDB = IDBPDatabase<AppDBSchema>;
export type AppDBNames = StoreNames<AppDBSchema>;
export type AppDBKey<Name extends AppDBNames> = StoreKey<AppDBSchema, Name>;
export type AppDBValue<Name extends AppDBNames> = StoreValue<AppDBSchema, Name>;

let openingAppDB: Promise<AppDB> | undefined;
/** While the databases are being deleted (see {@link suspendAppDB}) */
let appDBSuspension: Promise<void> | undefined;

export async function getDB(): Promise<AppDB> {
    while (appDBSuspension !== undefined) {
        await appDBSuspension;
    }
    if (openingAppDB === undefined) {
        openingAppDB = _openDB();
    }

    return openingAppDB;
}

/**
 * Keeps this page from opening the app database until `until` settles, so that it does not recreate the database while
 * the databases are being deleted (`clearAllCaches()`): {@link getDB} waits, and the deletion of the database does not
 * reload the page (the deletion reloads it once done).
 */
export function suspendAppDB(until: Promise<unknown>): void {
    const suspension: Promise<void> = until.then(
        () => undefined,
        () => undefined,
    ).then(() => {
        if (appDBSuspension === suspension) {
            appDBSuspension = undefined;
        }
    });
    appDBSuspension = suspension;
}

/** Closes the connection of this page to the app database, if any: a deletion waits for every connection to close */
export async function closeAppDB(): Promise<void> {
    const opening = openingAppDB;
    openingAppDB = undefined;
    if (opening !== undefined) {
        try {
            (await opening).close();
        } catch {
            // the database never opened, there is no connection to close
        }
    }
}

/**
 * Deletes the app database.
 *
 * The connection held by this page is closed first: a deletion requested while it is still open
 * fires `versionchange` on it, which `blocking()` reports as another page upgrading the database.
 */
export async function deleteAppDB(): Promise<void> {
    await closeAppDB();

    // called right after close() so that no getDB() can reopen a connection in between
    await deleteDB(DB_NAME);
}

/**
 * Deletes the local IndexedDB database so it is recreated at the version requested by the current app.
 *
 * Used to recover from an {@link IndexedDBVersionError} when the cached database has a higher version
 * than the running app (e.g. after downgrading to an older app version). This discards local data that
 * has not been synchronized yet.
 */
export async function downgradeDatabase(): Promise<void> {
    await deleteAppDB();
}

async function _openDB(): Promise<AppDB> {
    const db = await openDB<AppDBSchema>(DB_NAME, DB_VERSION, {
        async upgrade<AppTDBSchema>(db, oldVersion,version,transaction) {
            if (oldVersion < 1) {
                const v1Db = (db as unknown) as IDBPDatabase<AppDBSchema_V1>;

                v1Db.createObjectStore('Projects', { keyPath: 'projectId' });
                v1Db.createObjectStore('ProjectPatches', { keyPath: 'patchId' });
            }

            if (oldVersion < 2) {
                const v2Db = (db as unknown) as IDBPDatabase<AppDBSchema_V2>;

                v2Db.createObjectStore('Interactions', { keyPath: 'interactionId' });
            }

            if (oldVersion < 3) {
                const v3Db = (db as unknown) as IDBPDatabase<AppDBSchema_V3>;

                v3Db.createObjectStore('UserReportTemplateConfigurations', { keyPath: 'reportTemplateId' });
            }
            if (oldVersion < 4) {
                const projectPatchesObjectStore = transaction.objectStore( 'ProjectPatches' );
                projectPatchesObjectStore.createIndex( 'nature', 'nature', { unique: false } );
                const patches = await projectPatchesObjectStore.getAll();
                for ( const patch of patches ) {
                    if ( patch.nature ) {
                        continue;
                    }
                    const realPatchId = [ ...patch.patchId.split( '|' ) ].pop();
                    switch ( realPatchId ) {
                        case 'current':
                            patch.nature = PatchNature.TempPatch;
                            break;
                        default:
                            patch.nature = PatchNature.PatchData;
                            break;
                    }
                    projectPatchesObjectStore.put( patch );
                }
            }
            if (oldVersion < 5) {
                const v5Db = (db as unknown) as IDBPDatabase<AppDBSchema_V5>;
                v5Db.createObjectStore('ProjectParticipants', { keyPath: 'projectId' });
                v5Db.createObjectStore('UserCache', { keyPath: 'key' });
                v5Db.createObjectStore('Dependencies', { keyPath: 'key' });
                v5Db.createObjectStore('ProjectDependencies', { keyPath: 'key' });
                v5Db.createObjectStore('AttachmentCache', { keyPath: 'key' });
            }

        },

        blocked() {
            const err = new UpgradeBlockedIndexedDBError();
            ErrorHandlerService.handleError(err);
        },

        terminated() {
            Navigation.driver.reload(true);
        },
    });

    // another page asks for a new version of the database, or deletes it (`newVersion` is null): it waits for this
    // connection to close
    db.addEventListener('versionchange', (event) => {
        if (event.newVersion === null) {
            // deleted with everything the app keeps (`Archipad.Commands.clearAllCaches()`): this page starts again too,
            // once the deletion is done when it knows about it (suspended)
            db.close();
            openingAppDB = undefined;
            if (appDBSuspension === undefined) {
                Navigation.driver.reload(true);
            }
            return;
        }
        ErrorHandlerService.handleError(new BlockingUpgradeIndexedDBError());
    });
    return db;
}

/**
 * Returns a new IDBKeyRange for a given prefix to perform a "startsWith" operation
 * on IndexedDB.
 * 
 * We use a range from a lower bound with the searchString and an upper bound with
 * the searchString + the highest DOMString character to exclude all keys that
 * don't exactly start with the searchString.
 * 
 * @see https://hacks.mozilla.org/2014/06/breaking-the-borders-of-indexeddb/#:~:text=startsWith(str)
 */
export function startsWithIDBKeyRange(searchString: string): IDBKeyRange {
    const MAX_CHARACTER = '\uffff';
    return IDBKeyRange.bound(searchString, searchString + MAX_CHARACTER);
}
